/* Copyright (c) 2022 Xperi. All Rights Reserved.  */

import PropTypes from 'prop-types';
import React, { FunctionComponent, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    DEFAULT_CONTENT_DURATION_IN_MILLISECONDS,
    MILLISECONDS_IN_A_SECOND,
    PROGRESS_BAR_LENGTH,
    PROGRESS_BAR_LENGTH_720P,
    SECONDS_IN_A_MINUTE,
    TICK_DURATION,
    TRICK_PLAY_DISPLAY_TIME,
    TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME,
    HALF_HOUR_IN_MILLISECONDS,
    MIN_CACHE_IN_MILLISECONDS,
    DEFAULT_SEEK_RANGE_IN_MILLISECONDS,
} from '../../../config';
import { AppUtils } from '../../../core/AppUtils';
import { DeviceFeatures } from '../../../core/device/DeviceFeatures';
import { DeviceModel } from '../../../core/device/DeviceModel';
import { Locale } from '../../../core/locale/Locale';
import { LocaleKeys } from '../../../core/locale/LocaleKeys';
import { QoEAppLogger } from '../../../core/logging/qoe/QoEAppLogger';
import { VuLogger } from '../../../core/logging/VULogger';
import { NetworkManager } from '../../../core/network/NetworkManager';
import { PlaybackContentType } from '../../../core/socu/enums/PlaybackContentType';
import { SocuDataModelImpl } from '../../../core/socu/impls';
import { VideoManager } from '../../../core/videoplayer';
import { TrickplayImpl } from '../../../core/videoplayer/impls';
import { VideoPlayerModel } from '../../../core/videoplayer/interfaces';
import { RootState } from '../../../store/root.reducers';
import { videoPlayerIsMediaButtonPressedAction } from '../../../store/videoplayer/videoplayer.actions';
import { toggleIsPlaying } from '../../../store/videoplayer/videoplayer.reducers';
import { updateTrickPlayMenuTimer } from '../../../store/videoplayerui/infobanner/infobanner.reducers';
import {
    PlaybackState,
    TrickPlayType,
} from '../../../store/videoplayerui/infobanner/PlaybackTypes';
import { InfoBannerUtils } from '../FullInfoBanner/EventDescription/InfoBannerUtils';
import { MediaButtonsPanel } from './MediaButtons';
import { TrickplayButtonProps } from './TrickplayButtonProps';
import TrickplayOsd from './TrickplayOsd';

import './TrickplayBanner.scss';

const TrickplayBanner: FunctionComponent<TrickplayButtonProps> = ({ isAdult }): JSX.Element => {
    const dispatch = useDispatch();
    const { playbackState, channel, mediaButtonPressed } = useSelector(
        (state: RootState) => state.videoPlayer,
    );
    const { airingOffers, offerPreview } = VideoManager.getInstance().getMetadata();
    const oVideoPlayerModelImplInst: VideoPlayerModel =
        VideoManager.getInstance().getVideoPlayerModel();
    const dataFormatter = new InfoBannerUtils(airingOffers, offerPreview);
    const tickmarkRef: any = useRef(null);
    const commonOffset = useRef(0);
    const {
        allowRewind = false,
        allowPause = false,
        allowFastForward = false,
    } = oVideoPlayerModelImplInst.trickModeRestrictions() || {};
    const isTrickplayEnable = oVideoPlayerModelImplInst.isTrickplayEnable(playbackState);
    const isBufferThresholdReached = oVideoPlayerModelImplInst.isBufferThresholdReached();
    const isAvailableInSocu = dataFormatter.getIsAvailableInSocu();
    const isSocuEnabled = DeviceFeatures.isSocuEnabled();
    const { isSubscribed } = channel;

    const getCurrentTimeLocal = () => {
        if (SocuDataModelImpl.getInstance().isStartOver()) {
            const currentTime =
                oVideoPlayerModelImplInst.getCurrentTimeInMs() -
                oVideoPlayerModelImplInst.getSeekRangeStartTimeInMs();
            return currentTime;
        }
        return oVideoPlayerModelImplInst.getCurrentTimeInMs();
    };

    const [currentTime, setCurrentTime] = useState(getCurrentTimeLocal() || new Date().getTime());

    const calculatePlaybackStartEndTime = () => {
        const cacheSize =
            oVideoPlayerModelImplInst.getSeekRangeInMs() || DEFAULT_SEEK_RANGE_IN_MILLISECONDS;
        const oSocuDataModelImpl = SocuDataModelImpl.getInstance();
        if (oSocuDataModelImpl.isStartOver()) {
            const showingDuration =
                (oSocuDataModelImpl.getActionDetails()?.kernel?.duration || 0) *
                MILLISECONDS_IN_A_SECOND;
            const duration = showingDuration > cacheSize ? showingDuration : cacheSize;
            return { startT: 0, endT: duration };
        }
        if (oSocuDataModelImpl.isCatchUp()) {
            return { startT: 0, endT: cacheSize };
        }
        const finalCacheSize =
            cacheSize <= MIN_CACHE_IN_MILLISECONDS ? HALF_HOUR_IN_MILLISECONDS : cacheSize;
        // Subtract CACHE_SIZE from the current time and round DOWN to the nearest half.
        let startTime = Date.now() - finalCacheSize;
        startTime = Math.floor(startTime / HALF_HOUR_IN_MILLISECONDS) * HALF_HOUR_IN_MILLISECONDS;
        // Start Time plus CACHE_SIZE plus 30 minutes, rounded UP to the nearest half hour.
        let endTime = startTime + finalCacheSize + HALF_HOUR_IN_MILLISECONDS;
        endTime = Math.ceil(endTime / HALF_HOUR_IN_MILLISECONDS) * HALF_HOUR_IN_MILLISECONDS;
        return { startT: startTime, endT: endTime };
    };

    const [playbackStartEndTime, setPlaybackStartEndTime] = useState(
        calculatePlaybackStartEndTime(),
    );

    const { startT, endT } = playbackStartEndTime;

    const getCacheStartTime = () => {
        if (SocuDataModelImpl.getInstance().isSocu()) {
            return 0;
        }
        return oVideoPlayerModelImplInst.getSeekRangeStartTimeInMs();
    };

    const bufferStartTime = getCacheStartTime() || 0;

    const getCacheEndTime = () => {
        if (SocuDataModelImpl.getInstance().isSocu()) {
            return oVideoPlayerModelImplInst.getSeekRangeInMs();
        }
        return oVideoPlayerModelImplInst.getSeekRangeEndTimeInMs();
    };

    const bufferEndTime = getCacheEndTime() || 0;

    const calculateCurrentTime = () => {
        if (SocuDataModelImpl.getInstance().isSocu()) {
            return AppUtils.formatTimeInHM(getCurrentTimeLocal());
        }
        return AppUtils.formatTime(getCurrentTimeLocal());
    };

    let currentFormatedTime = calculateCurrentTime();

    const calculateStartTime = () => {
        if (SocuDataModelImpl.getInstance().isSocu()) {
            return '';
        }
        return (startT && AppUtils.formatTime(startT)) || '';
    };

    const startTime = calculateStartTime();

    const calculateEndTime = () => {
        if (SocuDataModelImpl.getInstance().isSocu()) {
            return (endT && AppUtils.formatTimeInHM(endT)) || '';
        }
        return (endT && AppUtils.formatTime(endT)) || '';
    };

    const endTime = calculateEndTime();

    const calculatePosition = () => {
        if (SocuDataModelImpl.getInstance().isSocu()) {
            return Number(
                (
                    ((DeviceModel.is1080P() ? PROGRESS_BAR_LENGTH : PROGRESS_BAR_LENGTH_720P) *
                        currentTime) /
                    (endT || DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
                ).toFixed(3),
            );
        }
        return Number(
            (
                ((DeviceModel.is1080P() ? PROGRESS_BAR_LENGTH : PROGRESS_BAR_LENGTH_720P) *
                    (currentTime - startT)) /
                (startT && endT ? endT - startT : DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
            ).toFixed(3),
        );
    };

    const calculateCacheStartPosition = () => {
        if (SocuDataModelImpl.getInstance().isSocu()) {
            return 0;
        }
        if (startT && endT && bufferStartTime) {
            let startPositionTime = bufferStartTime - startT;
            if (!allowRewind && !allowFastForward) {
                return 0;
            }
            if (allowFastForward && !allowRewind) {
                startPositionTime = currentTime - startT;
            }
            return Number(
                (
                    ((DeviceModel.is1080P() ? PROGRESS_BAR_LENGTH : PROGRESS_BAR_LENGTH_720P) *
                        startPositionTime) /
                    (endT - startT || DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
                ).toFixed(3),
            );
        }
        return 0;
    };

    const calculateCacheEndPosition = () => {
        if (SocuDataModelImpl.getInstance().isSocu()) {
            if (endT && bufferEndTime) {
                return Number(
                    (
                        ((DeviceModel.is1080P() ? PROGRESS_BAR_LENGTH : PROGRESS_BAR_LENGTH_720P) *
                            bufferEndTime) /
                        (endT || DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
                    ).toFixed(3),
                );
            }
            return 0;
        }
        if (startT && endT && bufferEndTime) {
            let endPositionTime = bufferEndTime - startT;
            if (!allowFastForward && !allowRewind) {
                return 0;
            }
            if (allowRewind && !allowFastForward) {
                endPositionTime = currentTime - startT;
            }
            return Number(
                (
                    ((DeviceModel.is1080P() ? PROGRESS_BAR_LENGTH : PROGRESS_BAR_LENGTH_720P) *
                        endPositionTime) /
                    (endT - startT || DEFAULT_CONTENT_DURATION_IN_MILLISECONDS)
                ).toFixed(3),
            );
        }
        return 0;
    };

    const [currentPosition, setCurrentPosition] = useState(calculatePosition());
    const [cacheStartPosition, setcacheStartPosition] = useState(calculateCacheStartPosition());
    const [cacheEndPosition, setcacheEndPosition] = useState(calculateCacheEndPosition());

    const setPlaybackIcon = useCallback(() => {
        switch (playbackState) {
            case PlaybackState.RW3:
                return <img className="media-rw3-icon" />;
            case PlaybackState.RW2:
                return <img className="media-rw2-icon" />;
            case PlaybackState.RW1:
                return <img className="media-rw1-icon" />;
            case PlaybackState.PAUSE:
                return <img className="media-pause-icon" />;
            case PlaybackState.PLAY:
                return <img className="media-play-icon" />;
            case PlaybackState.FF3:
                return <img className="media-ff3-icon" />;
            case PlaybackState.FF2:
                return <img className="media-ff2-icon" />;
            case PlaybackState.FF1:
                return <img className="media-ff1-icon" />;
            default:
                return <></>;
        }
    }, [playbackState]);

    const refreshCurrentTime = () => {
        const playerCurrentTime = getCurrentTimeLocal() || Date.now();
        setCurrentTime(playerCurrentTime);
        currentFormatedTime = calculateCurrentTime();
    };

    useEffect(() => {
        if (tickmarkRef && tickmarkRef.current) {
            commonOffset.current = parseInt(
                window.getComputedStyle(tickmarkRef.current).getPropertyValue('left'),
                10,
            );
        }
        setPlaybackStartEndTime(calculatePlaybackStartEndTime());
        refreshCurrentTime();
    }, []);

    /**
     * This use effect will be called when the buffer end time changes(Live, StartOver).
     * Playback start time and end time will be changed based on
     * the program duration from SOCU offer and the duration from the player.
     * If the program duration time from the SOCU offer is less than
     * the duration from the player, then show the duration from the SOCU offer;
     * otherwise, show the duration from the player.
     */
    useEffect(() => {
        const cacheEndTime = bufferEndTime || Date.now();
        if (cacheEndTime > endT) {
            setPlaybackStartEndTime(calculatePlaybackStartEndTime());
        }
    }, [bufferEndTime, endT]);

    useEffect(() => {
        const timerId = setInterval(
            refreshCurrentTime,
            isTrickplayEnable ? TRICKPLAY_BAR_KEY_DISPATCH_INTERVAL_TIME : MILLISECONDS_IN_A_SECOND,
        );
        return () => {
            clearInterval(timerId);
        };
    }, [isTrickplayEnable]);

    useEffect(() => {
        setCurrentPosition(calculatePosition());
        setcacheStartPosition(calculateCacheStartPosition());
        setcacheEndPosition(calculateCacheEndPosition());

        if (!SocuDataModelImpl.getInstance().isSocu()) {
            const programStartTime = new Date(dataFormatter.getStartTime()).getTime();
            const programEndTime = dataFormatter.getEndTime().getTime();
            if (currentTime > programEndTime || currentTime < programStartTime) {
                VideoManager.getInstance().refreshMetadata(currentTime);
                QoEAppLogger.logProgramEnded();
                VuLogger.logWatchStoppedEvent(VuLogger.WatchStoppedEndReason.END_OF_CONTENT);
            }
        }
    }, [currentTime]);

    useEffect(() => {
        if (isTrickplayEnable && isBufferThresholdReached) {
            if (playbackState !== PlaybackState.PLAY) dispatch(toggleIsPlaying());
            refreshCurrentTime();
            dispatch(updateTrickPlayMenuTimer());
        }
    }, [isBufferThresholdReached]);

    useEffect(() => {
        let pauseNotSupportTimer: any = null;
        if (mediaButtonPressed.isMediaButtonPressed) {
            pauseNotSupportTimer = setTimeout(() => {
                dispatch(
                    videoPlayerIsMediaButtonPressedAction({
                        type: TrickPlayType.NONE,
                        isMediaButtonPressed: false,
                    }),
                );
            }, TRICK_PLAY_DISPLAY_TIME);
        }
        return () => {
            clearTimeout(pauseNotSupportTimer);
        };
    }, [mediaButtonPressed]);

    const calculateNumberOfTicks = () => {
        const trickplayImplIns = new TrickplayImpl(
            VideoManager.getInstance().getPlayable(),
            oVideoPlayerModelImplInst.getPlaybackIntervalInfo(),
        );
        const duration = trickplayImplIns.getProgramDurationInSeconds() / SECONDS_IN_A_MINUTE;
        if (duration < TICK_DURATION) {
            return 1;
        }
        return Math.ceil(duration / trickplayImplIns.calculateDivisorFromDurationInMin(duration));
    };

    const numOfTicks = useMemo(() => {
        return calculateNumberOfTicks();
    }, [endT]);

    const renderTickmarks = (index: number, offset: number) => (
        <div key={`tickmark-${index + 1}`} className={'tickmark'} style={{ left: offset }}></div>
    );

    // Default tickmark to show until playback first frame renders.
    const getDefaultTickmarks = () => {
        const progressBarLength = DeviceModel.is1080P()
            ? PROGRESS_BAR_LENGTH
            : PROGRESS_BAR_LENGTH_720P;
        const tickOffset = progressBarLength;
        const offset = commonOffset.current + tickOffset;
        return renderTickmarks(0, offset);
    };

    const getTickmarks = () => {
        const trickplayImplIns = new TrickplayImpl(
            VideoManager.getInstance().getPlayable(),
            oVideoPlayerModelImplInst.getPlaybackIntervalInfo(),
        );
        return Array(numOfTicks)
            .fill(undefined)
            .map((_value, index) => {
                const offset = trickplayImplIns.calculateDynamicTickMarkOffset(
                    commonOffset.current,
                    index,
                    numOfTicks,
                );
                return renderTickmarks(index, offset);
            });
    };

    const checkConditions = () => {
        return (
            isSubscribed &&
            !isAdult &&
            oVideoPlayerModelImplInst.isPlaybackStarted() &&
            !NetworkManager.isNetworkDisconnected()
        );
    };
    const checkConditionsWithoutPlay = () => {
        return (
            isSubscribed &&
            !isAdult &&
            // TODO: Enable button if player state LOADED,
            // change as per player state listener approach.
            !NetworkManager.isNetworkDisconnected()
        );
    };

    const checkStartFromBufferButton = () => {
        if (checkConditionsWithoutPlay() && isSocuEnabled && isAvailableInSocu) {
            return false;
        }
        return allowRewind && checkConditions()
            ? oVideoPlayerModelImplInst.isBufferThresholdReachedStart()
            : true;
    };

    const checkPlayPauseButton = () => {
        if (checkConditions() && (allowPause || isTrickplayEnable)) {
            return false;
        }
        return true;
    };

    const checkFFAdvanceButton = () => {
        return checkConditions() && !oVideoPlayerModelImplInst.isBufferThresholdReachedEnd()
            ? !allowFastForward
            : true;
    };

    const checkRWReplyButton = () => {
        return checkConditions() && !oVideoPlayerModelImplInst.isBufferThresholdReachedStart()
            ? !allowRewind
            : true;
    };

    const checkLiveButton = () => {
        return (
            SocuDataModelImpl.getInstance().getPlaybackContentType() === PlaybackContentType.LIVE
        );
    };

    const getMediaButtonRestrictionStr = () => {
        if (mediaButtonPressed.isMediaButtonPressed) {
            switch (mediaButtonPressed.type) {
                case TrickPlayType.REPLAY:
                    return Locale.getString(LocaleKeys.LIVE_TV_REPLAY_NOT_ALLOWED);
                case TrickPlayType.RW:
                    return Locale.getString(LocaleKeys.LIVE_TV_REWIND_NOT_ALLOWED);
                case TrickPlayType.PAUSE:
                    return Locale.getString(LocaleKeys.LIVE_TV_PAUSE_NOT_SUPPORTED_MESSAGE);
                case TrickPlayType.FF:
                    return Locale.getString(LocaleKeys.LIVE_TV_FAST_FORWARD_NOT_ALLOWED);
                case TrickPlayType.ADVANCE:
                    return Locale.getString(LocaleKeys.LIVE_TV_ADVANCE_NOT_ALLOWED);
                default:
                    return '';
            }
        }
        return '';
    };

    return (
        <>
            <div className="media-container" data-testid="media-buttons-container">
                <div className="media-dropshadow-down"></div>
                <div className="media-progress-bar-container">
                    {checkConditions() && (
                        <div
                            className="media-progress-bar-cache"
                            style={
                                cacheStartPosition || cacheEndPosition
                                    ? {
                                          left: cacheStartPosition,
                                          width: Math.abs(cacheEndPosition - cacheStartPosition),
                                      }
                                    : {}
                            }
                        ></div>
                    )}
                    {checkConditions() && (
                        <div
                            className="media-current-time-tickmark"
                            style={{ left: currentPosition }}
                        ></div>
                    )}
                    <span className="media-start-time">{checkConditions() ? startTime : ''}</span>
                    <div ref={tickmarkRef} key={'tickmark'} className={'tickmark'}></div>
                    {checkConditions() ? getTickmarks() : getDefaultTickmarks()}
                    <div className="media-progress-bar-empty"></div>
                    <span className="media-end-time">{checkConditions() ? endTime : ''}</span>
                </div>
                <div className="media-trick-play-container">
                    {checkConditions() && !mediaButtonPressed.isMediaButtonPressed ? (
                        <div
                            className="media-current-time-container"
                            style={{ left: currentPosition }}
                        >
                            <span className="media-current-time">{currentFormatedTime}</span>
                            {setPlaybackIcon()}
                        </div>
                    ) : (
                        <span className="media-pause-not-suppoted-container">
                            {mediaButtonPressed.isMediaButtonPressed && (
                                <img className="media-pause-not-suppoted-icon" />
                            )}
                            {mediaButtonPressed.isMediaButtonPressed && (
                                <span className="media-pause-not-suppoted-text">
                                    {getMediaButtonRestrictionStr()}
                                </span>
                            )}
                        </span>
                    )}
                </div>
                <MediaButtonsPanel
                    isAvailableInSocu={isAvailableInSocu}
                    isPlayPauseButtonDisabled={checkPlayPauseButton()}
                    isForwardButtonDisabled={checkFFAdvanceButton()}
                    isRewindButtonDisabled={checkRWReplyButton()}
                    isAdvanceButtonDisabled={checkFFAdvanceButton()}
                    isReplayButtonDisabled={checkRWReplyButton()}
                    isGoToLiveButtonDisabled={
                        allowFastForward && checkConditions()
                            ? oVideoPlayerModelImplInst.isBufferThresholdReachedEnd()
                            : true
                    }
                    isStartFromBufferButtonDisabled={checkStartFromBufferButton()}
                    isLiveButtonDisabled={checkLiveButton()}
                />
            </div>
            {checkConditions() && isTrickplayEnable && <TrickplayOsd />}
        </>
    );
};

TrickplayBanner.propTypes = {
    isAdult: PropTypes.bool,
};

export default TrickplayBanner;
